import { defineMessages, type IntlShape } from 'react-intl';
import type {
  BlockConfigBase,
  BlocksFormData,
  Brain,
  JSONSchema,
} from '@plone/types';
import { addCtaFieldset } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';
import type { CtaBlockExtender } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';

import addBlocksFullWidth from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksFullWidth';
import addBlockLines from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlockLines';

export interface BannerData
  extends JSONSchema,
    BlocksFormData,
    CtaBlockExtender {
  '@type': 'banner';
  bannerImage?: Brain;
  bannerTitle?: string;
  bannerDescription?: string;
  showOverlay?: boolean;
  bannerTitleAsH1?: boolean;
  bannerSize: string;
  styles?: {
    line_color?: string;
    fullwidth?: boolean;
    add_bottom_line?: boolean;
    add_top_line?: boolean;
  };
}

const BannerSchema = ({
  data,
  intl,
}: {
  data: BannerData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: 'Banner',
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: [
          'bannerImage',
          'bannerSize',
          'bannerTitle',
          'bannerDescription',
          'showOverlay',
          'bannerTitleAsH1',
        ],
      },
    ],
    properties: {
      bannerImage: {
        title: intl.formatMessage(messages.bannerImage),
        widget: 'object_browser',
        mode: 'image',
        return: 'single',
      },
      bannerSize: {
        title: intl.formatMessage(messages.bannerSize),
        widget: 'size_widget',
        choices: ['Small', 'Large'],
      },
      bannerTitle: {
        title: intl.formatMessage(messages.bannerTitle),
      },
      bannerDescription: {
        title: intl.formatMessage(messages.bannerDescription),
        widget: 'textarea',
      },
      showOverlay: {
        title: intl.formatMessage(messages.showOverlay),
        description: intl.formatMessage(messages.showOverlayDescr),
        type: 'boolean',
      },
      bannerTitleAsH1: {
        title: intl.formatMessage(messages.bannerTitleAsH1),
        description: intl.formatMessage(messages.bannerTitleAsH1Description),
        type: 'boolean',
      },
    },
    required: [],
  };

  addCtaFieldset({ schema, intl });
  addBlocksFullWidth({ schema, formData: data, intl });
  addBlockLines({ schema, formData: data, intl });

  return schema;
};

const messages = defineMessages({
  style: { id: 'style', defaultMessage: 'Block Style' },
  bannerImage: { id: 'bannerImage', defaultMessage: 'Banner image' },
  bannerTitle: { id: 'bannerTitle', defaultMessage: 'Banner title' },
  bannerSize: { id: 'bannerSize', defaultMessage: 'Banner size' },
  bannerDescription: {
    id: 'bannerDescription',
    defaultMessage: 'Banner description',
  },
  bannerLink: { id: 'bannerLink', defaultMessage: 'Banner link' },
  bannerLinkText: { id: 'bannerLinkText', defaultMessage: 'Banner link text' },
  showOverlay: { id: 'showOverlay', defaultMessage: 'Show overlay' },
  showOverlayDescr: {
    id: 'showOverlayDescr',
    defaultMessage: 'Show dark overlay to make the banner text more visible',
  },
  bannerTitleAsH1: {
    id: 'bannerTitleAsH1',
    defaultMessage: 'Set banner title as page title',
  },
  bannerTitleAsH1Description: {
    id: 'bannerTitleAsH1Description',
    defaultMessage:
      'Set the banner title as the page title by rendering it as an <h1> html tag ',
  },
});

export default BannerSchema;

export interface BannerConfig extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof BannerSchema;
}
